import i18n from './i18n';
// Must be a static import so the OAuth listener is registered before Amplify
// processes any OAuth callback in the URL (dynamic import would be too late).
// Safe in mock mode — no OAuth events fire when Amplify is not configured.
import 'aws-amplify/auth/enable-oauth-listener';
import { PnetAuthProvider, type PnetAuthConfig } from '@asi-lda/pnet-auth-web/react';
import { amplifyConfig } from './amplifyconfiguration';
import { isMockAuthEnabled } from '@/config/authMode';
import { authTokenKind } from '@/config/authToken';
import { authSessionMode, identityApiUrl, SESSION_CLIENT_KEY } from '@/config/authSession';
import { SessionRegistry } from '@/auth/sessionRegistry';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { I18nextProvider } from 'react-i18next';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { ToastContainer } from 'react-toastify';
import useZoomDetection from '@/utils/zoomDetection';
import { LicenseInfo } from '@mui/x-license';
import { SidebarNavigationProvider } from '@/contexts/sidebarNavigationContext';
import { LoadingProvider } from '@/contexts/LoadingContext';
import ConfirmationProvider from '@/provider/ConfirmationProvider';
import { StyledEngineProvider } from '@mui/system';
import Pages from './routes';
import { ThemeComponentProvider } from '@/components/shared/Theme/ThemeComponent';
import '@/components/main.scss';
import '@/components/modern-overrides.scss';
import '@asi-lda/pnet-auth-web/styles.css';
import { GlobalFiltersProvider } from '@/hooks/useGlobalFilters';

// Shared login/SSO kit. The provider calls `Amplify.configure` exactly once
// for this config object (Cognito mode only); in mock mode Amplify is never
// configured and the login screen posts the gateway's `mock/login`.
// CheckEmail / LoginData / Logout go to the identity service when
// VITE_IDENTITY_API_URL is set (moved from the gateway); VITE_AUTH_SESSION=cookie
// keeps the refresh token in its httpOnly cookie (src/config/authSession.ts).
const authConfig: PnetAuthConfig = {
  amplify: amplifyConfig ?? undefined,
  tokenKind: authTokenKind,
  gatewayBaseUrl: import.meta.env.VITE_API_URL,
  ...(identityApiUrl ? { identityBaseUrl: identityApiUrl } : {}),
  session: authSessionMode,
  sessionClient: SESSION_CLIENT_KEY,
  authMode: isMockAuthEnabled ? 'mock' : 'cognito',
};

if (isMockAuthEnabled) {
  // eslint-disable-next-line no-console
  console.info('[AUTH_MODE] Running UI in local mock mode. Cognito is disabled in this runtime.');
}

function App() {
  const isZoomedIn = useZoomDetection();

  useEffect(() => {
    if (import.meta.env.VITE_MUI_LICENSE_KEY) {
      LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD) {
      return undefined;
    }

    const preventContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const preventRestrictedKeys = (event: KeyboardEvent) => {
      if (
        event.key === 'F12' ||
        (event.ctrlKey && event.shiftKey && event.key === 'I') ||
        (event.ctrlKey && event.shiftKey && event.key === 'J') ||
        (event.ctrlKey && event.key === 'U')
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventRestrictedKeys);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventRestrictedKeys);
    };
  }, []);

  return (
    <PnetAuthProvider config={authConfig}>
      <SessionRegistry />
      <I18nextProvider i18n={i18n}>
        <StyledEngineProvider injectFirst>
          <ThemeComponentProvider>
            <ConfirmationProvider />
            <LoadingProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <SidebarNavigationProvider>
                  <GlobalFiltersProvider>
                    <div className={isZoomedIn ? 'App browserZoom' : 'App'}>
                      <ToastContainer stacked />
                      <Pages />
                    </div>
                  </GlobalFiltersProvider>
                </SidebarNavigationProvider>
              </LocalizationProvider>
            </LoadingProvider>
          </ThemeComponentProvider>
        </StyledEngineProvider>
      </I18nextProvider>
    </PnetAuthProvider>
  );
}

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<App />);
